import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import Layout from "@/components/Layout";
import { Plus, Search, Filter, FileText, Calendar, User, Eye, Edit, Trash2, AlertTriangle, Shield, Trophy, Building2, CheckCircle2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { AOPermissionsDialog } from "@/components/AOPermissionsDialog";
import { useAuth } from "@/hooks/useAuth";
import { appelsOffresService, societesService, depotsService } from "@/services";
import type { AppelOffre } from "@/types";

interface AppelOffreWithStats extends AppelOffre {
  societes_evaluees?: number;
  societes_total?: number;
  statut?: 'en cours' | 'publié' | 'terminé';
}

const AppelsOffres = () => {
  const [appelOffres, setAppelOffres] = useState<AppelOffreWithStats[]>([]);
  const [filteredAO, setFilteredAO] = useState<AppelOffreWithStats[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [societeFilter, setSocieteFilter] = useState("all");
  const [typeAnalyseFilter, setTypeAnalyseFilter] = useState("all");
  const [societes, setSocietes] = useState<string[]>([]);
  const [aoSocietesMap, setAoSocietesMap] = useState<Record<string, string[]>>({});
  const [typesAnalyse, setTypesAnalyse] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAO, setSelectedAO] = useState<AppelOffreWithStats | null>(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [selectedAOForPermissions, setSelectedAOForPermissions] = useState<AppelOffreWithStats | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { userRole } = useAuth();

  useEffect(() => {
    loadAppelsOffres();
    loadSocietes();
    loadAoSocietesMap();
  }, []);

  useEffect(() => {
    filterAppelsOffres();
  }, [appelOffres, searchTerm, statusFilter, societeFilter, typeAnalyseFilter, aoSocietesMap]);

  const loadAppelsOffres = async () => {
    try {
      const data = await appelsOffresService.list();

      // Transform data to match expected format
      const aoWithStats: AppelOffreWithStats[] = data.map(ao => ({
        ...ao,
        statut: (ao as any).statut || 'en cours',
        societes_evaluees: 0,
        societes_total: 0,
      }));

      setAppelOffres(aoWithStats);

      // Extract unique prompt template names for filter
      const uniqueTypes = Array.from(
        new Set(
          data.map(ao => ao.promptTemplate?.name).filter(Boolean)
        )
      ).sort();
      setTypesAnalyse(uniqueTypes as string[]);

    } catch (error) {
      console.error('Erreur lors du chargement des appels d\'offres:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les appels d'offres",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadSocietes = async () => {
    try {
      const data = await societesService.list();
      const uniqueSocietes = data.map(s => s.nom).sort();
      setSocietes(uniqueSocietes);
    } catch (error) {
      console.error('Erreur lors du chargement des sociétés:', error);
    }
  };

  const loadAoSocietesMap = async () => {
    try {
      const depots = await depotsService.list();
      const map: Record<string, string[]> = {};
      for (const depot of depots) {
        const societeName = depot.candidat?.societe?.nom;
        if (societeName) {
          if (!map[depot.aoId]) map[depot.aoId] = [];
          if (!map[depot.aoId].includes(societeName)) {
            map[depot.aoId].push(societeName);
          }
        }
      }
      setAoSocietesMap(map);
    } catch (error) {
      console.error('Erreur lors du chargement de la map AO-sociétés:', error);
    }
  };

  const filterAppelsOffres = () => {
    let filtered = appelOffres;

    // Filter by text
    if (searchTerm) {
      filtered = filtered.filter(ao =>
        ao.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ao.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ao.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(ao => ao.statut === statusFilter);
    }

    // Filter by société
    if (societeFilter !== "all") {
      filtered = filtered.filter(ao => aoSocietesMap[ao.id]?.includes(societeFilter));
    }

    // Filter by analysis type
    if (typeAnalyseFilter !== "all") {
      filtered = filtered.filter(ao => ao.promptTemplate?.name === typeAnalyseFilter);
    }

    setFilteredAO(filtered);
  };

  const getStatusBadge = (statut?: string) => {
    switch (statut) {
      case 'en cours':
        return <Badge className="bg-warning text-warning-foreground whitespace-nowrap">En cours</Badge>;
      case 'publié':
        return <Badge className="bg-success text-success-foreground whitespace-nowrap">Publié</Badge>;
      case 'terminé':
        return <Badge className="bg-destructive text-destructive-foreground whitespace-nowrap">Terminé</Badge>;
      default:
        return <Badge variant="outline" className="whitespace-nowrap">En cours</Badge>;
    }
  };

  const handleDelete = async () => {
    if (!selectedAO) return;
    try {
      await appelsOffresService.delete(selectedAO.id);
      toast({
        title: "Suppression réussie",
        description: "L'appel d'offres a été supprimé"
      });
      loadAppelsOffres();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer l'appel d'offres",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setSelectedAO(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement des appels d'offres...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="font-bold text-foreground text-xl">Gestion des Analyses</h1>
            <p className="text-muted-foreground">Gérez vos analyses et leurs critères d'évaluation</p>
          </div>
          {userRole === 'admin' && (
            <Button onClick={() => navigate('/appels-offres/nouveau')}>
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle analyse
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="card-shadow">
          <CardHeader className="bg-slate-100 py-3 px-4">
            <CardTitle className="text-base">Filtres</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher par titre, référence ou description..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="en cours">En cours</SelectItem>
                  <SelectItem value="publié">Publié</SelectItem>
                  <SelectItem value="terminé">Terminé</SelectItem>
                </SelectContent>
              </Select>

              <Select value={societeFilter} onValueChange={setSocieteFilter}>
                <SelectTrigger className="w-full">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Société" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="all">Toutes les sociétés</SelectItem>
                  {societes.map((societe) => (
                    <SelectItem key={societe} value={societe}>
                      {societe}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeAnalyseFilter} onValueChange={setTypeAnalyseFilter}>
                <SelectTrigger className="w-full">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Type d'analyse" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="all">Type d'analyse</SelectItem>
                  {typesAnalyse.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card className="card-shadow">
          <CardHeader className="rounded-sm">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  Analyses ({filteredAO.length})
                </CardTitle>
                <CardDescription>Liste de toutes vos analyses</CardDescription>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/ranking-societes')}
              >
                <Trophy className="h-4 w-4 mr-2" />
                Ranking Sociétés
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredAO.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                  {appelOffres.length === 0 ? "Aucun appel d'offres" : "Aucun résultat"}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {appelOffres.length === 0 ? "Commencez par créer votre premier appel d'offres" : "Essayez d'ajuster vos filtres de recherche"}
                </p>
                {appelOffres.length === 0 && userRole === 'admin' && (
                  <Button onClick={() => navigate('/appels-offres/nouveau')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un A.O.
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAO.map(ao => (
                  <div key={ao.id} className="flex items-center justify-between p-4 border border-card-border rounded-lg hover:bg-muted/50 transition-smooth">
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-primary mt-1" />
                          <div>
                            <h4 className="font-semibold text-lg">{ao.titre}</h4>
                            <p className="text-sm text-muted-foreground">
                              Réf: {ao.reference}
                            </p>
                            {ao.description && (
                              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                {ao.description}
                              </p>
                            )}
                            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                              <div className="flex items-center">
                                <Calendar className="h-3 w-3 mr-1" />
                                Limite: {formatDate(ao.dateLimite)}
                              </div>
                              <div className="flex items-center">
                                <User className="h-3 w-3 mr-1" />
                                Créé le {formatDate(ao.createdAt)}
                              </div>
                              {(ao.societes_total ?? 0) > 0 && (
                                <div className="flex items-center">
                                  <Building2 className="h-3 w-3 mr-1" />
                                  <span className={ao.societes_evaluees === ao.societes_total ? "text-success" : ""}>
                                    {ao.societes_evaluees}/{ao.societes_total} sociétés évaluées
                                  </span>
                                  {ao.societes_evaluees === ao.societes_total && ao.societes_total > 0 && (
                                    <CheckCircle2 className="h-3 w-3 ml-1 text-success" />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(ao.statut)}

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/appels-offres/${ao.id}`)}
                            className="h-8 w-8 p-0"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {userRole === 'admin' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/appels-offres/${ao.id}/edit`)}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedAOForPermissions(ao);
                                  setPermissionsDialogOpen(true);
                                }}
                                className="h-8 w-8 p-0"
                                title="Gérer les accès"
                              >
                                <Shield className="h-4 w-4" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedAO(ao);
                                  setDeleteDialogOpen(true);
                                }}
                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="card-shadow">
          <AlertDialogHeader className="text-center pb-4">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <AlertDialogTitle className="text-xl font-semibold">
              Supprimer l'appel d'offres
            </AlertDialogTitle>
            <AlertDialogDescription className="text-left mt-4 space-y-3">
              <div className="bg-muted/30 p-4 rounded-lg border">
                <p className="font-medium text-foreground mb-2">Appel d'offres à supprimer :</p>
                <p className="text-sm">
                  <span className="font-medium">Titre :</span> {selectedAO?.titre}
                </p>
                <p className="text-sm">
                  <span className="font-medium">Référence :</span> {selectedAO?.reference}
                </p>
              </div>

              <div className="bg-destructive/5 p-4 rounded-lg border border-destructive/20">
                <p className="text-sm text-destructive font-medium mb-2">⚠️ Attention : Cette action est irréversible</p>
                <p className="text-sm text-muted-foreground">
                  La suppression de cet appel d'offres entraînera également la suppression de :
                </p>
                <ul className="text-sm text-muted-foreground mt-2 ml-4 space-y-1">
                  <li>• Tous les CV associés</li>
                  <li>• Toutes les analyses effectuées</li>
                  <li>• L'historique complet du projet</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3 pt-4">
            <AlertDialogCancel className="flex-1">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="flex-1 bg-destructive text-destructive-foreground hover:bg-destructive/90 focus:ring-destructive"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permissions Dialog */}
      {selectedAOForPermissions && (
        <AOPermissionsDialog
          open={permissionsDialogOpen}
          onOpenChange={setPermissionsDialogOpen}
          aoId={selectedAOForPermissions.id}
          aoTitle={selectedAOForPermissions.titre}
        />
      )}
    </Layout>
  );
};

export default AppelsOffres;
